import { Capacitor } from "@capacitor/core";

const COMPRESS_MIN_BYTES = 12 * 1024 * 1024; // small clips are sent as-is

export const nativeVideoPickAvailable = () => Capacitor.isNativePlatform();

/** Native gallery picker (iOS/Android) → picked videos as device paths. */
export async function pickNativeVideos(limit = 10) {
  const { FilePicker } = await import("@capawesome/capacitor-file-picker");
  const res = await FilePicker.pickVideos({ limit, readData: false, skipTranscoding: true });
  return (res.files || []).filter((f) => f.path).map((f) => ({ path: f.path, name: f.name || "video.mp4", size: f.size || 0, mimeType: f.mimeType || "video/mp4" }));
}

/** Lightweight reference to a file on the device — read in Range chunks at upload time, never fully in memory. */
async function pathToRef(path, name, type, knownSize) {
  const url = Capacitor.convertFileSrc(path);
  let size = knownSize || 0;
  if (!size) {
    try {
      const { Filesystem } = await import("@capacitor/filesystem");
      size = (await Filesystem.stat({ path })).size || 0;
    } catch (e) { /* fall through */ }
  }
  if (!size) {
    const head = await fetch(url, { headers: { Range: "bytes=0-0" } });
    const cr = head.headers.get("Content-Range") || "";
    size = parseInt(cr.split("/")[1] || "0", 10) || parseInt(head.headers.get("Content-Length") || "0", 10);
  }
  if (!size) throw new Error("size unknown");
  return { native: true, path, url, name, size, type: type || "video/mp4" };
}

/**
 * Compress a picked video on the device (720p, hardware encoder) before upload.
 * onProgress(0..1). Falls back to the original when compression fails or does not help.
 * Returns { file, compressed, originalSize }.
 */
export async function compressPickedVideo(picked, onProgress, { skip = false } = {}) {
  const name = (picked.name || "video").replace(/\.[a-z0-9]+$/i, "") + ".mp4";
  if (skip || (picked.size && picked.size < COMPRESS_MIN_BYTES)) {
    return { file: await pathToRef(picked.path, picked.name, picked.mimeType, picked.size), compressed: false, originalSize: picked.size };
  }
  let listener = null;
  try {
    const { NativeVideoCompressor } = await import("capacitor-native-video-compressor");
    listener = await NativeVideoCompressor.addListener("onProgress", (info) => {
      if (info.status === "progress" && onProgress) onProgress(Math.max(0, Math.min(1, (info.percent || 0) / 100)));
    });
    const out = await NativeVideoCompressor.compressVideo({ sourcePath: picked.path, quality: "HIGH" });
    if (!out?.destPath) throw new Error("no output");
    const file = await pathToRef(out.destPath, name, "video/mp4");
    if (picked.size && file.size >= picked.size * 0.95) {
      return { file: await pathToRef(picked.path, picked.name, picked.mimeType, picked.size), compressed: false, originalSize: picked.size };
    }
    return { file, compressed: true, originalSize: picked.size || file.size };
  } catch (e) {
    return { file: await pathToRef(picked.path, picked.name, picked.mimeType, picked.size), compressed: false, originalSize: picked.size };
  } finally {
    try { listener && listener.remove(); } catch (e) { /* ignore */ }
  }
}
